import React, { useState } from "react";
import { 
  MicOff, 
  UserMinus, 
  Star, 
  XCircle, 
  Shield,
  MoreVertical,
  Flag,
  Ban
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArenaParticipant } from "@/hooks/useLiveArenas";
import { toast } from "@/hooks/use-toast";

interface ArenaHostControlsProps {
  isHost: boolean;
  participant: ArenaParticipant;
  onMuteParticipant: (userId: string) => void;
  onRemoveParticipant: (userId: string) => void;
  onSpotlightUser: (userId: string) => void;
  onReport: (userId: string) => void;
  onBlock: (userId: string) => void;
}

export const ArenaHostControls: React.FC<ArenaHostControlsProps> = ({
  isHost,
  participant,
  onMuteParticipant,
  onRemoveParticipant,
  onSpotlightUser,
  onReport,
  onBlock,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleMute = () => {
    onMuteParticipant(participant.user_id);
    toast({
      title: "Participant muted",
      description: `${participant.profile?.name} has been muted`,
    });
    setIsOpen(false);
  };

  const handleRemove = () => {
    onRemoveParticipant(participant.user_id);
    toast({
      title: "Participant removed",
      description: `${participant.profile?.name} has been removed from the arena`,
    });
    setIsOpen(false);
  };

  const handleSpotlight = () => {
    onSpotlightUser(participant.user_id);
    toast({
      title: "Spotlight on!",
      description: `${participant.profile?.name} is now spotlighted`,
    });
    setIsOpen(false);
  };

  const handleReport = () => {
    onReport(participant.user_id);
    toast({
      title: "Report submitted",
      description: "Thank you for helping keep the community safe",
    });
    setIsOpen(false);
  };

  const handleBlock = () => {
    onBlock(participant.user_id);
    toast({
      title: "User blocked",
      description: `${participant.profile?.name} has been blocked`,
    });
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 rounded-full bg-black/20 hover:bg-black/40"
        >
          <MoreVertical className="w-3.5 h-3.5 text-white" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {isHost && (
          <>
            <DropdownMenuItem onClick={handleMute} className="gap-2">
              <MicOff className="w-4 h-4" />
              <span>Mute participant</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleRemove} className="gap-2 text-destructive">
              <UserMinus className="w-4 h-4" />
              <span>Remove from arena</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleSpotlight} className="gap-2">
              <Star className="w-4 h-4 text-amber-500" />
              <span>Spotlight user</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={handleReport} className="gap-2">
          <Flag className="w-4 h-4" />
          <span>Report</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleBlock} className="gap-2 text-destructive">
          <Ban className="w-4 h-4" />
          <span>Block user</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface EndArenaButtonProps {
  onEndArena: () => void;
}

export const EndArenaButton: React.FC<EndArenaButtonProps> = ({ onEndArena }) => {
  return (
    <Button
      variant="destructive"
      size="sm"
      onClick={onEndArena}
      className="gap-2"
    >
      <XCircle className="w-4 h-4" />
      End Arena
    </Button>
  );
};

export default ArenaHostControls;
